import React from "react";

const VocabularySelectionModal = React.memo(({
  vocabularyList,
  selectedWords,
  searchQuery,
  setSearchQuery,
  toggleWordSelection,
  selectAllWords,
  clearAllSelection,
  getFilteredWordsForSelection,
  startCustomReview,
  setFlashcardModalStep
}) => {
  return (
    <>
      <div className="mb-6">
        <h3 className="text-xl font-black font-mono text-cyan-400 mb-4">
          SELECT VOCABULARY FOR TESTING
        </h3>
        
        {/* Search Bar */}
        <div className="mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search vocabulary..."
            className="w-full px-4 py-3 bg-black/60 border border-cyan-400/50 rounded-xl text-cyan-100 placeholder-cyan-500/70 focus:border-cyan-400 focus:shadow-neon-cyan transition-all duration-300 font-mono backdrop-blur-sm"
          />
        </div>

        {/* Selection Controls */}
        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={selectAllWords}
            className="px-4 py-2 bg-black/80 border border-green-400/50 rounded-lg text-green-300 hover:border-green-400 hover:shadow-neon-green transition-all duration-300 font-mono font-bold"
          >
            SELECT ALL ({getFilteredWordsForSelection().length})
          </button>
          <button
            onClick={clearAllSelection}
            className="px-4 py-2 bg-black/80 border border-red-400/50 rounded-lg text-red-300 hover:border-red-400 hover:shadow-neon-pink transition-all duration-300 font-mono font-bold"
          >
            CLEAR ALL
          </button>
          <div className="px-4 py-2 bg-black/80 border border-purple-400/50 rounded-lg text-purple-300 font-mono font-bold">
            SELECTED: {selectedWords.size}
          </div>
        </div>
      </div>

      {/* Word List */}
      <div className="max-h-60 overflow-y-auto mb-6 space-y-2">
        {getFilteredWordsForSelection().map((word) => (
          <div
            key={word.id}
            className={`p-4 rounded-xl border transition-all duration-300 cursor-pointer ${
              selectedWords.has(word.id)
                ? "bg-cyan-900/40 border-cyan-400/70 shadow-neon-cyan"
                : "bg-black/60 border-gray-600/50 hover:border-cyan-400/50"
            }`}
            onClick={() => toggleWordSelection(word.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-4">
                  <strong className="text-cyan-300 font-mono">
                    {word.wordJp}
                  </strong>
                  <span className="text-green-300 font-mono">
                    {word.wordVi}
                  </span>
                </div>
                {word.exampleSentence && (
                  <div className="text-sm text-gray-400 font-mono mt-1">
                    {word.exampleSentence}
                  </div>
                )}
                {/* Small Image Display */}
                {word.imageUrl && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="bg-black/40 border border-orange-400/30 rounded-lg p-2 flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3 text-orange-400">
                        <path fillRule="evenodd" d="M2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H4Z" clipRule="evenodd" />
                        <path fillRule="evenodd" d="M10.5 5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM12 9.5 9.5 7 6.5 10.5 5 9l-2 2.5h9Z" clipRule="evenodd" />
                      </svg>
                      <img
                        src={word.imageUrl}
                        alt={`Preview for ${word.wordJp}`}
                        className="w-8 h-8 rounded border border-orange-400/40 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'inline';
                        }}
                        onLoad={(e) => {
                          e.target.style.display = 'inline';
                          e.target.nextSibling.style.display = 'none';
                        }}
                      />
                      <span 
                        className="text-xs text-orange-300 font-mono"
                        style={{ display: 'none' }}
                      >
                        IMG
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center ${
                selectedWords.has(word.id)
                  ? "border-cyan-400 bg-cyan-400"
                  : "border-gray-500"
              }`}>
                {selectedWords.has(word.id) && (
                  <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-between">
        <button
          onClick={() => setFlashcardModalStep("options")}
          className="px-6 py-3 bg-black/80 border border-gray-400/50 rounded-lg font-mono font-bold text-gray-300 hover:border-gray-400 hover:text-gray-100 transition-all duration-300"
        >
          BACK
        </button>
        <button
          onClick={startCustomReview}
          disabled={selectedWords.size === 0}
          className="px-6 py-3 bg-black/80 border border-cyan-400/50 rounded-lg font-mono font-bold text-cyan-300 hover:border-cyan-400 hover:shadow-neon-cyan-intense hover:text-cyan-100 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          START REVIEW ({selectedWords.size} WORDS)
        </button>
      </div>
    </>
  );
});

export default VocabularySelectionModal; 